import { useState, useCallback } from 'react';
import type { ProductOption } from '../types/productTypes';
import { debug, warn } from '../utils/debug';

interface FilterState {
  price: string; // Now in format "min-max" (e.g., "200-800")
  brand: string;
  size: string;
  homeButton: string;
}

interface UseProductFilterResult {
  filters: FilterState;
  handleFilterChange: (filterType: string, value: string) => void;
  getFilteredProducts: (products: ProductOption[]) => ProductOption[];
  getReplacementProducts: (selectedProductIds: string[], filteredProducts: ProductOption[]) => string[];
  resetFilters: () => void;
}

/**
 * Custom hook to manage product filtering
 */
export function useProductFilter(): UseProductFilterResult {
  // Filter states with defaults
  const [filters, setFilters] = useState<FilterState>({
    price: '0-1200', // Default is full price range
    brand: 'brand_all',
    size: 'size_all',
    homeButton: 'home_all'
  });

  /**
   * Update a specific filter value
   */
  const handleFilterChange = useCallback((filterType: string, value: string) => {
    switch (filterType) {
      case 'price':
        setFilters(prev => ({ ...prev, price: value }));
        break;
      case 'brand':
        setFilters(prev => ({ ...prev, brand: value }));
        break;
      case 'size':
        setFilters(prev => ({ ...prev, size: value }));
        break;
      case 'home_button':
        setFilters(prev => ({ ...prev, homeButton: value }));
        break;
      default:
        warn(`Unknown filter type: ${filterType}`);
    }
  }, []);

  /**
   * AIDEV-NOTE: Product Filtering Logic 
   * This filtering implementation relies on product name pattern matching rather than metadata.
   * 
   * Challenges:
   * 1. Filter logic relies on product naming conventions which could break if naming changes
   * 2. Complex multi-select size filtering with overlapping conditions
   * 3. Home button detection uses a regex to extract model number and compare it
   * 
   * AIDEV-TODO: Replace name-based filtering with metadata-based filtering once proper
   * product attributes are available through metafields or structured data
   */
  const getFilteredProducts = useCallback((availableProducts: ProductOption[]): ProductOption[] => {
    return availableProducts.filter(product => {
      const productName = product.name.toLowerCase();
      
      // Price filtering using min-max range
      if (filters.price !== '0-1200') {
        // Parse min and max values from price filter
        const [minPriceStr, maxPriceStr] = filters.price.split('-');
        const minPrice = parseInt(minPriceStr || '0', 10);
        const maxPrice = parseInt(maxPriceStr || '1200', 10);
        
        // Use the actual price data from ProductOption
        const productPrices = product.prices || [];
        
        // If no price data is available, use fallback values
        let lowestPrice = 799;
        let highestPrice = 999;
        
        if (productPrices.length > 0) {
          // The prices array is already sorted in ascending order
          lowestPrice = productPrices[0] || lowestPrice;
          highestPrice = productPrices[productPrices.length - 1] || highestPrice;
        }
        
        // Apply min price filter: if the highest price is below min, filter out
        if (highestPrice < minPrice) {
          return false;
        }
        
        // Apply max price filter: if the lowest price is above max, filter out
        if (lowestPrice > maxPrice) {
          return false;
        }
      }

      // Brand filtering - handle multi-select
      if (filters.brand !== 'brand_all') {
        // Split the comma-separated brand values for multi-select support
        const selectedBrands = filters.brand.split(',');
        
        // Check if product matches any of the selected brands
        const productMatchesBrand = selectedBrands.some(brand => {
          if (brand === 'brand_apple' && productName.includes('iphone')) return true;
          if (brand === 'brand_samsung' && productName.includes('galaxy')) return true;
          if (brand === 'brand_google' && productName.includes('pixel')) return true;
          return false;
        });
        
        // If product doesn't match any selected brand, filter it out
        if (!productMatchesBrand) {
          return false;
        }
      }

      // Size filtering - handle multi-select using phoneSize metafield
      if (filters.size !== 'size_all') {
        // Only filter using phoneSize metafield data
        if (!product.phoneSize) {
          return false; // Filter out if no phoneSize metafield
        }
        
        // Split the comma-separated size values
        const selectedSizes = filters.size.split(',');
        const phoneSize = product.phoneSize.toLowerCase();
        
        const matchesSmall = selectedSizes.includes('size_small') && 
          phoneSize.includes('small');
        
        const matchesMedium = selectedSizes.includes('size_medium') && 
          phoneSize.includes('medium');
        
        const matchesLarge = selectedSizes.includes('size_large') && 
          phoneSize.includes('large');
        
        // If none of the selected sizes match, filter out this product
        if (!matchesSmall && !matchesMedium && !matchesLarge) {
          return false;
        }
      }

      // Home button filtering using navigation metafield
      if (filters.homeButton !== 'home_all') {
        // Only filter using navigation metafield data
        if (!product.navigation) {
          return false; // Filter out if no navigation metafield
        }
        
        const navigation = product.navigation.toLowerCase();
        const hasHomeButton = navigation.includes('home button') || navigation.includes('home');
        
        if (filters.homeButton === 'home_yes' && !hasHomeButton) return false;
        if (filters.homeButton === 'home_no' && hasHomeButton) return false;
      }

      return true;
    });
  }, [filters]);

  /**
   * Find replacement products for selected products that are no longer in the filtered list.
   * This function maintains the original selection order but replaces products that have
   * been filtered out with new products from the filtered list that aren't already selected.
   * 
   * @param selectedProductIds - Array of currently selected product IDs
   * @param filteredProducts - Array of products that match the current filters
   * @returns Array of updated product IDs with replacements for filtered-out products
   */
  const getReplacementProducts = useCallback((
    selectedProductIds: string[], 
    filteredProducts: ProductOption[]
  ): string[] => {
    // Create a map of filtered product IDs for quick lookup
    const filteredProductIds = new Set(filteredProducts.map(p => p.id));
    
    // Create an array of available replacement products (not already selected)
    const availableReplacements = filteredProducts.filter(p => 
      !selectedProductIds.includes(p.id)
    );
    
    // Check which selected products need replacement
    const updatedSelection = [...selectedProductIds];
    let hasReplaced = false;
    
    // For each position in the selection
    for (let i = 0; i < updatedSelection.length; i++) {
      const currentProductId = updatedSelection[i];
      
      // If this position has a product and it's no longer in filtered results, replace it
      if (currentProductId && !filteredProductIds.has(currentProductId)) {
        debug(`Product ${currentProductId} is filtered out, needs replacement`);
        
        if (availableReplacements.length > 0) {
          // Get the first available replacement
          const replacement = availableReplacements.shift(); // Remove the replacement from available list
          if (replacement) {
            debug(`Replacing with ${replacement.id} (${replacement.name})`);
            updatedSelection[i] = replacement.id;
            hasReplaced = true;
          }
        } else {
          // If no replacement found, leave as is - the UI will handle this case
          debug(`No replacement found for position ${i}`);
        }
      }
    }
    
    return hasReplaced ? updatedSelection : selectedProductIds;
  }, []);

  /**
   * Reset all filters to their default values
   */
  const resetFilters = useCallback(() => {
    setFilters({
      price: '0-1200',
      brand: 'brand_all',
      size: 'size_all',
      homeButton: 'home_all'
    });
  }, []);

  return {
    filters,
    handleFilterChange,
    getFilteredProducts,
    getReplacementProducts,
    resetFilters
  };
}